import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import useHotkeyHandlers from '../components/hooks';
import StringDischargeTrendReport from '../components/string_discharge_trend_report';
import StringDischargeTrendGraph from '../components/string_discharge_trend_graph';
import { parseDateTime, formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { styled } from '@mui/material/styles';

const DataSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));

const STRING_NAV = {
	BACK: -1,
	FORWARD: 1,
}

// String Trend sub-page rendering (Location Name, Title, String Selection)
export default function StringDischargeTrend() {
	const { locationNum, eventDateTime } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [locationInfo, setLocationInfo] = useState({
		locationName: '',
		numStrings: 0,
	});
	
	const [selectedString, setSelectedString] = useState();	

	const [trendData, setTrendData] = useState({});
	
	const [isLoading, setIsLoading] = useState(true);
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	const numStringsRef = useRef();
	const selectedStringRef = useRef();
	const graphTypeRef = useRef();
	const eventDateObj = parseDateTime(eventDateTime);
	
	// useEffect(() => {
	// 	console.log(searchParams.get("string"));
	// }, [searchParams])

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		numStringsRef.current = locationInfo.numStrings;
	}, [locationInfo]);

	// Handle the current selectedString for hotkey nav
	useEffect(() => {
		selectedStringRef.current = selectedString;
	}, [selectedString]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for string and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				handleStringNavigation(STRING_NAV.BACK);
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				handleStringNavigation(STRING_NAV.FORWARD);
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Current
						setGraphType(1);
						break;
					case 1:  // Current -> Temperature
						setGraphType(2);
						break;
					case 2:  // Temperature -> Voltage
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);

	// Handle string navigation
	function handleStringNavigation(stringNav) {
		const numStrings = numStringsRef.current;
		const stringNum = selectedStringRef.current;

		if (selectedStringRef.current > 0) {
			if (stringNav === STRING_NAV.BACK && stringNum > 1) {  // Decrement string
				setSelectedString(Number(stringNum - 1));
			}

			if (stringNav === STRING_NAV.FORWARD && stringNum < numStrings) {  // Increment string
				setSelectedString(Number(stringNum + 1));
			}
		}
	}
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
				numStrings: locationInfo.num_strings,
			});
			setSelectedString(Number(searchParams.get("string")) || 1);
		});
	}, [locationNum]);
	
	// Get the list of trend data for the specified location id, string number, and unit number
	useEffect(() => {
		setIsLoading(true);

		if (selectedString !== undefined) {
			// Get String Trend Data
			axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/string_${selectedString}`).then(response => {				
				const trendData = response.data;
				setTrendData(trendData);		
			});
		}

		setIsLoading(false);
	}, [locationNum, eventDateTime, selectedString]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							String {selectedString} Discharge Trend : {formatDateTime(eventDateObj)}
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Current</option>
									<option key={2} value={2}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
							>
								{ Array.from({length: locationInfo.numStrings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<Tooltip title="Data display type">
							<Stack direction="row" spacing={1} alignItems="center">
								<Typography fontFamily='Saira, sans-serif'>Report</Typography>
									<DataSwitch checked={graphDisplay} onChange={handleViewChange} disabled={isLoading}/>
								<Typography fontFamily='Saira, sans-serif'>Graph</Typography>
							</Stack>
						</Tooltip>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<StringDischargeTrendGraph
					trendData={trendData}
					selectedString={selectedString}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<StringDischargeTrendReport
					trendData={trendData}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}